import { useState } from "react";
import TokenSummary from "../../../components/common/ReportBox";
import {
  Container,
  Paper,
  Box,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Grid from "@mui/material/Grid2";
import { GetAllSummaryReport } from "../../../lib/api";
import { useQuery } from "@tanstack/react-query";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#2552EB", "#4CAF50", "#FF9800", "#F44336", "#9C27B0"];

const Summary = () => {
  const [date, setDate] = useState(new Date());
  const formattedDate = date.toLocaleDateString("en-CA");
  const [isDownloadExcel, setIsDownloadExcel] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["GetAllSummaryReport", formattedDate],
    queryFn: () => GetAllSummaryReport(formattedDate),
  });

  const summaryData = data?.data?.summaryReport || {};
  console.log(summaryData, "summary data");

  const convertMinutesToHours = (minutes) => {
    if (!minutes || minutes <= 0) return "0 minutes";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
      return `${hrs} ${hrs === 1 ? "hour" : "hours"} ${
        mins > 0 ? `${mins} ${mins === 1 ? "min" : "mins"}` : ""
      }`;
    }
    return `${mins} ${mins === 1 ? "min" : "mins"}`;
  };

  const renderContent = (value, placeholder = "Loading...") => {
    if (isLoading) return placeholder;
    if (isError || value === undefined || value === null) return "N/A";
    return value;
  };

  // Prepare data for charts
  const performanceData = summaryData.CounterPerformanceData || [];
  const unservedData = [
    { name: "On Hold", value: summaryData.TokensOnHold || 0 },
    { name: "Pending", value: summaryData.TokensPending || 0 },
  ];

  const handleDownloadExcel = async () => {
    if (isLoading) return;
    if (!summaryData || Object.keys(summaryData).length === 0) {
      toast.error("No data to Download", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setIsDownloadExcel(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Token Summary Report");

      // Title
      worksheet.mergeCells("A1:B1");
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value = "Token Summary Report";
      titleRow.getCell(1).font = { bold: true, size: 14 };
      titleRow.getCell(1).alignment = { horizontal: "center" };

      // Date
      worksheet.mergeCells("A2:B2");
      worksheet.getRow(2).getCell(1).value = `Date: ${formattedDate}`;
      worksheet.getRow(2).getCell(1).alignment = { horizontal: "center" };

      worksheet.addRow([]);

      // Summary Statistics
      const statsHeader = worksheet.addRow(["Summary Statistics", ""]);
      statsHeader.font = { bold: true, size: 12 };
      statsHeader.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };

      worksheet.addRow([
        "Total Tokens Issued",
        summaryData.TotalTokensIssued || 0,
      ]);
      worksheet.addRow([
        "Average Wait Time",
        convertMinutesToHours(summaryData.AverageWaitTimeMinutes),
      ]);
      worksheet.addRow([]);

      // Peak Hour
      const peakHeader = worksheet.addRow(["Peak Hour", ""]);
      peakHeader.font = { bold: true, size: 12 };
      peakHeader.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };

      worksheet.addRow(["Hour", summaryData.PeakHour?.Hour || "N/A"]);
      worksheet.addRow([
        "Tokens Issued",
        summaryData.PeakHour?.TokensIssued || 0,
      ]);
      worksheet.addRow([]);

      // Counter Performance
      const perfHeader = worksheet.addRow(["Counter Performance", ""]);
      perfHeader.font = { bold: true, size: 12 };
      perfHeader.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };

      worksheet.addRow(["Counter Name", "Tokens Served"]);
      summaryData.CounterPerformanceData?.forEach((item) => {
        worksheet.addRow([item.CounterName, item.TokensServed]);
      });
      worksheet.addRow([]);

      // Unserved Tokens
      const unservedHeader = worksheet.addRow(["Unserved Tokens", ""]);
      unservedHeader.font = { bold: true, size: 12 };
      unservedHeader.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };

      worksheet.addRow(["On Hold", summaryData.TokensOnHold || 0]);
      worksheet.addRow(["Pending", summaryData.TokensPending || 0]);

      // Style the worksheet
      worksheet.columns = [
        { key: "label", width: 30 },
        { key: "value", width: 20 },
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Token_Summary_Report_${formattedDate}.xlsx`;
      saveAs(new Blob([buffer]), fileName);

      toast.success("Excel file downloaded successfully", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
    setIsDownloadExcel(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div role="status">
            <svg
              aria-hidden="true"
              className="w-12 h-12 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600 mx-auto"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
          <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
            Loading summary data...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="py-5">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h4 className="text-2xl md:text-3xl font-bold text-gray-800">
            Token Summary Report
          </h4>
          <p className="text-gray-500 text-sm mt-1">
            Overview of token performance and statistics
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Picker */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
              </svg>
            </div>
            <Flatpickr
              value={date}
              onChange={(selectedDate) => setDate(selectedDate[0])}
              options={{ dateFormat: "Y-m-d" }}
              className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
              placeholder="Select a date"
            />
          </div>

          {/* Excel Download Button */}
          <div className="relative">
            <button
              onClick={handleDownloadExcel}
              disabled={isDownloadExcel}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Export Excel
            </button>
            {isDownloadExcel && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded z-10">
                <div className="flex justify-center items-center m-auto">
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="w-6 h-6 text-gray-200 animate-spin fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          {/* Total Tokens Issued */}
          <Grid xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1 }}>
                Total Tokens Issued
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                {summaryData.TotalTokensIssued || 0}
              </Typography>
              <Box
                sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Chip
                  label="Today"
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                />
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {formattedDate}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Average Wait Time */}
          <Grid xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                color: "white",
              }}
            >
              <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1 }}>
                Average Wait Time
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {convertMinutesToHours(summaryData.AverageWaitTimeMinutes)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.7, mt: 1, display: "block" }}
              >
                Per token
              </Typography>
            </Paper>
          </Grid>

          {/* Peak Hour */}
          <Grid xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
              }}
            >
              <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1 }}>
                Peak Hour
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {summaryData.PeakHour?.Hour || "N/A"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                {summaryData.PeakHour?.TokensIssued || 0} tokens issued
              </Typography>
            </Paper>
          </Grid>

          {/* Unserved Tokens */}
          <Grid xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                color: "white",
              }}
            >
              <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1 }}>
                Unserved Tokens
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    On Hold
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {summaryData.TokensOnHold || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Pending
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {summaryData.TokensPending || 0}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Counter Performance Chart */}
          <Grid xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #e0e0e0",
                height: "100%",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
                Counter Performance
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="CounterName" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="TokensServed"
                    fill="#2552EB"
                    name="Tokens Served"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Unserved Tokens Pie Chart */}
          <Grid xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #e0e0e0",
                height: "100%",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
                Unserved Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={unservedData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {unservedData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Counter Performance Details */}
          <Grid xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
                Performance Details
              </Typography>
              <Grid container spacing={2}>
                {performanceData.map((item, index) => (
                  <Grid xs={12} sm={6} md={4} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "#f8f9fa",
                        borderRadius: "12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {item.CounterName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Tokens Served
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", color: "#2552EB" }}
                      >
                        {item.TokensServed}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default Summary;
